/**
 * Shared validation patterns used by both backend DTOs (class-validator
 * `@Matches`) and the console client (Angular `Validators.pattern`), so the two
 * sides can't drift.
 */

/**
 * A bare domain: no protocol, no spaces, at least one dot (e.g. `school.edu`).
 * Case-insensitive.
 *
 * Validated LABEL BY LABEL rather than as one character class. The previous
 * pattern used `[a-z0-9.-]+` for everything before the TLD, which let dots and
 * hyphens sit anywhere — so `bad..domain`, `.example.com`, `-bad.com` and
 * `bad-.com` were all accepted. `bad..domain` reached sandbox's authorized
 * domain list through the wizard before anyone noticed (2026-09-22).
 *
 * Each label must therefore start and end alphanumeric, with hyphens only in
 * between, and is capped at the DNS limit of 63 characters. One or more such
 * labels, then a letters-only TLD of at least two characters.
 */
export const BARE_DOMAIN_REGEX =
  /^(?!https?:\/\/)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

/**
 * User-facing error for a value that fails {@link BARE_DOMAIN_REGEX}. Shared so
 * the backend DTO (`@Matches` message) and the client-side pre-check show the
 * same wording.
 */
export const BARE_DOMAIN_ERROR = 'Enter a valid domain (e.g. example.edu).';

/**
 * A Google Workspace customer id, e.g. `C02c9bmnt` — a leading `C` followed by
 * the account identifier. Case-insensitive.
 */
export const GOOGLE_CUSTOMER_ID_REGEX = /^C[0-9a-z]{6,}$/i;

/**
 * Consumer / public email domains that can never be a Gopher customer's
 * workspace domain. Used to (1) short-circuit license lookups for personal
 * logins (licensing) and (2) reject them as authorized onboarding domains
 * (onboarding Step 2). Shared here so both backends read one list.
 */
export const IGNORED_CONSUMER_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com',
  'googlemail.com',
  'google.com',
  'yahoo.com',
  'hotmail.com',
]);
